import type { GroupingGroup, GroupingItem } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React from 'react'

import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import {
  chainName,
  FieldArray,
  FieldColorPicker,
  FieldFile,
  FieldInput,
  FormField,
} from 'components/formKit'
import { IconButton, TileButton } from 'components/uiKit/Button'
import Divider from 'components/uiKit/Divider'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { t } from 'services/Translation'
import { FileTypeEnum } from 'utils/fileTypes'
import { getFileByUsage } from 'utils/files'

import GroupingFormValueAddAnswer from './GroupingFormValueAddAnswer'
import {
  useGroupingFormValueUpdateForm,
  useGroupingFormValueContext,
} from './GroupingFormValueContext'
import GroupingFormValueItemAnswer from './GroupingFormValueItemAnswer'
import * as s from './GroupingFormValueItemGroup.module.scss'

const DEFAULT_ANSWER: Omit<GroupingItem, 'value'> = {
  label: t('elements.grouping.form.answer'),
  image: null,
  overlay: '',
}

const GROUP_LABEL = genElementValueField({
  as: FieldInput,
  layout: 'vertical',
  name: 'target.label',
  label: t('elements.grouping.form.group.name'),
  props: { placeholder: t('input.label.group') },
})

const GROUP_IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'target.image',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
})

const GROUP_OVERLAY = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'target.overlay',
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

const ITEMS = genElementValueField({
  as: FieldArray<GroupingItem>,
  name: 'items',
  props: {
    RenderItem: GroupingFormValueItemAnswer,
    RenderAdd: GroupingFormValueAddAnswer,
    defaultItem: DEFAULT_ANSWER,
    min: 1,
    max: 5,
  },
})

const ANSWER_LABEL = genElementValueField({
  as: FieldInput,
  name: 'label',
  size: KitSize.M,
  props: { placeholder: t('elements.grouping.form.answer') },
})

const ANSWER_IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'image',
  layout: 'vertical',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
})

const ANSWER_OVERLAY = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'overlay',
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

interface IProps {
  name: string
  index: number
  group: GroupingGroup
}

const RootHeader: React.FC<IProps> = ({ index }) => {
  return (
    <Typography weight='medium'>
      {t('elements.grouping.tags.group')} {index + 1}
    </Typography>
  )
}

const RootPanel: React.FC<IProps> = ({ name, index, group }) => {
  const setForm = useGroupingFormValueUpdateForm()
  const file = getFileByUsage(group.target.image, FileUsageTypeEnum.image)
  const handleSetImage = () => setForm({ form: 'image' })

  return (
    <Padding gap>
      <FormField config={chainName(name, `${index}`, GROUP_LABEL)} />
      <TileButton
        icon='otherAdd'
        name={`${name}.image`}
        onClick={handleSetImage}
        styleType='stroke'
      >
        <PanelIconImage file={group.target.image} />
        <Typography rows={1} styleType='hint' tooltip={file?.name}>
          {file?.name || t('input.label.image')}
        </Typography>
      </TileButton>
      <Typography weight='medium'>{t('elements.grouping.tags.answers')}</Typography>
      <FormField config={chainName(name, `${index}`, ITEMS)} />
    </Padding>
  )
}

const ImageHeader: React.FC<IProps> = ({ index }) => {
  const setForm = useGroupingFormValueUpdateForm()
  const handleBack = () => setForm({ form: 'root' })

  return (
    <span className={s.floatingHeader}>
      <IconButton
        icon='back20Px'
        iconSize={KitSize.S}
        name='mainMenuBackButton'
        onClick={handleBack}
        size={KitSize.XS}
        styleType='ghost'
      />
      <Typography color='var(--color-placeholder)' onClick={handleBack} weight='medium'>
        {t('elements.grouping.tags.group')} {index + 1}
      </Typography>
      <Divider styleType='slash' free />
      <Typography weight='medium'> {t('input.label.image')}</Typography>
    </span>
  )
}

const ImagePanel: React.FC<IProps> = ({ name, index }) => {
  return (
    <Padding gap>
      <FormField config={chainName(name, `${index}`, GROUP_IMAGE)} />
      <FormField config={chainName(name, `${index}`, GROUP_OVERLAY)} />
    </Padding>
  )
}

const AnswerHeader: React.FC<IProps> = ({ index }) => {
  const setForm = useGroupingFormValueUpdateForm()
  const { answerIndex = 0 } = useGroupingFormValueContext()
  const handleBack = () => setForm({ form: 'root' })

  return (
    <span className={s.floatingHeader}>
      <IconButton
        icon='back20Px'
        iconSize={KitSize.S}
        name='mainMenuBackButton'
        onClick={handleBack}
        size={KitSize.XS}
        styleType='ghost'
      />
      <Typography color='var(--color-placeholder)' onClick={handleBack} weight='medium'>
        {t('elements.grouping.tags.group')} {index + 1}
      </Typography>
      <Divider styleType='slash' free />
      <Typography weight='medium'>
        {t('elements.grouping.form.answer')} {answerIndex + 1}
      </Typography>
    </span>
  )
}

const AnswerPanel: React.FC<IProps> = () => {
  const { answerName, answerIndex } = useGroupingFormValueContext()

  return (
    <Padding gap>
      <FormField config={chainName(answerName, `${answerIndex}`, ANSWER_LABEL)} />
      <Typography weight='medium'>{t('input.label.image')}</Typography>
      <FormField config={chainName(answerName, `${answerIndex}`, ANSWER_IMAGE)} />
      <FormField config={chainName(answerName, `${answerIndex}`, ANSWER_OVERLAY)} />
    </Padding>
  )
}

export default {
  root: { header: RootHeader, panel: RootPanel },
  image: { header: ImageHeader, panel: ImagePanel },
  answer: { header: AnswerHeader, panel: AnswerPanel },
}
